import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useAddMCPServer } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { DEFAULT_RESOURCE_VISIBILITY, type ResourceVisibility } from 'types/visibility';
import { getToolFormInputClassName, getToolFormTextareaClassName } from '../tool-form-styles';
import { APPROVAL_TO_VALUE, HumanApprovalField, type HumanApprovalOption } from './human-approval';

type AddMcpServerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectKey: string;
};

type AddMcpServerFormData = {
  toolName: string;
  description: string;
  visibility: ResourceVisibility;
  isOpenTool: boolean;
  isBlocksAuthenticated: boolean;
  humanApproval: HumanApprovalOption;
};

export function AddMcpServerModal({
  open,
  onOpenChange,
  projectKey,
}: AddMcpServerModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = projectKey === process.env.REACT_APP_PUBLIC_X_BLOCKS_KEY;
  const { mutateAsync: addMCPServer, isPending } = useAddMCPServer();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    control,
    watch,
  } = useForm<AddMcpServerFormData>({
    mode: 'onChange',
    defaultValues: {
      toolName: '',
      description: '',
      visibility: DEFAULT_RESOURCE_VISIBILITY,
      isOpenTool: false,
      isBlocksAuthenticated: false,
      humanApproval: 'auto',
    },
  });

  const isBlocksToolChecked = watch('isOpenTool');

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
    }
    onOpenChange(nextOpen);
  };

  const onSubmit = async (data: AddMcpServerFormData) => {
    if (!projectKey) {
      toast({
        variant: 'destructive',
        title: 'Missing project key',
        description: 'Unable to create the MCP server for this tenant.',
      });
      return;
    }

    try {
      const response = await addMCPServer({
        name: data.toolName.trim(),
        description: data.description.trim(),
        project_key: projectKey,
        visibility: data.visibility,
        is_open_tool: isAdmin ? data.isOpenTool : false,
        is_blocks_authenticated: isAdmin ? data.isBlocksAuthenticated : false,
        requires_approval: APPROVAL_TO_VALUE[data.humanApproval],
      });

      if (!response.is_success) {
        throw new Error(response.message || response.detail || 'Failed to create MCP server');
      }

      toast({
        variant: 'success',
        title: 'MCP server created',
        description: response.detail || 'MCP server created successfully.',
      });
      await queryClient.invalidateQueries({ queryKey: ['tools'] });
      handleClose(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Create failed',
        description: error instanceof Error ? error.message : 'Failed to create MCP server.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[640px] rounded-[0.5rem] border-0 p-4 shadow-xl">
        <DialogHeader className="space-y-1 px-0 py-0">
          <DialogTitle className="text-lg font-medium leading-none">Add MCP server</DialogTitle>
          <DialogDescription className="text-[13px] leading-6">
            Define how this MCP server will assist your agent in performing tasks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-0 py-4">
          <div className="space-y-2">
            <label htmlFor="toolName" className="text-[13px] font-medium leading-6">
              Tool Name <span className="text-destructive">*</span>
            </label>
            <Input
              id="toolName"
              placeholder="Enter name"
              {...register('toolName', {
                required: 'Tool name is required',
                setValueAs: (value) => value.trim(),
              })}
              className={getToolFormInputClassName(!!errors.toolName)}
            />
            {errors.toolName && (
              <p className="text-[13px] leading-6 text-destructive">{errors.toolName.message}</p>
            )}
          </div>

          <Controller
            name="visibility"
            control={control}
            render={({ field }) => (
              <VisibilityField
                value={field.value}
                onValueChange={field.onChange}
                idPrefix="add-mcp-server-visibility"
                disabled={isPending}
              />
            )}
          />

          <div className="space-y-2">
            <label htmlFor="description" className="text-[13px] font-medium leading-6">
              Description <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="description"
              rows={4}
              placeholder="Description of this tool for the AI to use in conversations"
              {...register('description', {
                required: 'Description is required',
                setValueAs: (value) => value.trim(),
              })}
              className={getToolFormTextareaClassName(!!errors.description)}
            />
            {errors.description && (
              <p className="text-[13px] leading-6 text-destructive">{errors.description.message}</p>
            )}
          </div>

          <HumanApprovalField
            control={control}
            name="humanApproval"
            id="addMcpHumanApproval"
            toolType="mcp"
            disabled={isPending}
          />

          {isAdmin && (
            <div className="space-y-4 pt-1">
              <div className="flex items-center gap-2">
                <Controller
                  name="isOpenTool"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      id="isOpenTool"
                      checked={field.value}
                      onChange={field.onChange}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  )}
                />
                <label htmlFor="isOpenTool" className="cursor-pointer text-[13px] font-medium leading-6">
                  Blocks Tool
                </label>
              </div>

              {isBlocksToolChecked && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Controller
                      name="isBlocksAuthenticated"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          id="isBlocksAuthenticated"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      )}
                    />
                    <label
                      htmlFor="isBlocksAuthenticated"
                      className="cursor-pointer text-[13px] font-medium leading-6"
                    >
                      Allow Tool Authentication
                    </label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger type="button" className="cursor-help">
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-[13px] leading-6">
                            If checked, users can copy the tool with its authentication.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch id="mcpVisibility" checked={false} disabled className="cursor-not-allowed" />
                    <label htmlFor="mcpVisibility" className="text-[13px] font-medium leading-6">
                      Tool Visibility
                    </label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger type="button" className="cursor-help">
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-[13px] leading-6">
                            Admin can edit this MCP server later to enable it.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-0 py-0">
          <Button
            variant="ghost"
            className="h-8 rounded-[6px] px-3 text-[13px] text-foreground hover:bg-transparent"
            onClick={() => handleClose(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            className="h-8 rounded-[6px] px-3 text-[13px]"
            onClick={handleSubmit(onSubmit)}
            disabled={isPending || !isValid}
          >
            {isPending ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
